// src/Favorites.js
import React, { useState, useEffect } from 'react';
import UtilFunctions from './UtilFunctions';
import Header from './components/Header';
import Footer from './components/Footer';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import ListGroup from 'react-bootstrap/ListGroup';
import Background from './images/class-search-background.png';
import serviceauth from './auth/serviceauth';
import ClassRow from './components/ClassRow';
import CourseItem from './components/CourseItem';
import Form from 'react-bootstrap/Form';
import Dropdown from 'react-bootstrap/Dropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { Spinner } from '@simply007org/react-spinners';
import SpinnerImage from 'react-bootstrap/Spinner';
import { spinnerService } from '@simply007org/react-spinners';
import MediaQuery from 'react-responsive';

const jumbotronBackground = {
  backgroundPosition: '0% 68%',
  backgroundSize: 'cover',
  background:
    'linear-gradient(to top, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0)), url(' +
    Background +
    ')',
};

const Favorites = () => {
  // State to control rendering until auth check is complete
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // State for all user favorites from API
  const [favorites, setFavorites] = useState([]);
  // State for favorites filtered by current search type (class/course)
  const [filteredFavorites, setFilteredFavorites] = useState([]);
  // State for unique terms available in filtered favorites
  const [termList, setTermList] = useState([]);
  // State for currently selected terms in dropdown
  const [selectedTerms, setSelectedTerms] = useState([]);
  // State for class/course data organized by term
  const [favoriteItemsByTerm, setFavoriteItemsByTerm] = useState(new Map());
  // State for current search type toggle
  const [searchType, setSearchType] = useState('class'); // 'class' or 'course'
  // State for navigation breadcrumb data
  const [navTree, setNavTree] = useState(null);
  // State to track newly added term for scrolling
  const [newlyAddedTerm, setNewlyAddedTerm] = useState(null);
  // Ref to track current search type for async operations
  const currentSearchTypeRef = React.useRef('class');
  // State to track if initial data loading is complete
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  // State to track if this is the first visit to prevent auto-selection
  const [isFirstVisit, setIsFirstVisit] = useState(true);
  // State to track if we're switching search types
  const [isSwitchingSearchType, setIsSwitchingSearchType] = useState(false);

  // Check authentication immediately
  useEffect(() => {
    if (!sessionStorage.getItem(serviceauth.SS_JWT_TOKEN)) {
      window.location.replace(serviceauth.redirectToServiceauthUrl(true));
    } else {
      setIsAuthenticated(true);
    }
  }, []);

  // Keep ref in sync with search type
  useEffect(() => {
    currentSearchTypeRef.current = searchType;
  }, [searchType]);

  // Get initial toggle and term from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlType = params.get('type');
    const urlTerm = params.get('term');

    // Set search type from URL if valid
    if (urlType === 'courses' || urlType === 'course') {
      setSearchType('course');
    } else if (urlType === 'class' || urlType === 'classes') {
      setSearchType('class');
    }

    // Set selected term from URL if provided
    if (urlTerm) {
      setSelectedTerms([urlTerm]);
    }
  }, []);

  // Load navigation breadcrumb data on component mount
  useEffect(() => {
    const fetchNavTree = async () => {
      const tree = await UtilFunctions.getHeaderNavTree('', 'favorites');
      setNavTree(tree);
    };
    fetchNavTree();
  }, []);

  // Load all user favorites from API on component mount
  useEffect(() => {
    const fetchFavorites = async () => {
      const result = await getFavorites();
      const output = result?.output || [];
      setFavorites(output);
      setIsInitialLoadComplete(true);
    };
    fetchFavorites();
  }, []);

  // Update termList and filteredFavorites when favorites or type changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // Process favorites data when favorites or search type changes
  useEffect(() => {
    const currentSearchType = searchType; // Capture current search type

    // Filter favorites by current search type (class vs course)
    const filtered = favorites.filter((fav) => fav.type === searchType);
    // console.log('Filtered favorites:', filtered);
    setFilteredFavorites(filtered);

    // Extract unique terms and sort them (newest first)
    const terms = [...new Set(filtered.map((f) => f.term))].sort().reverse();
    setTermList(terms);

    // Auto-select all terms on first visit or when switching search types
    if (
      selectedTerms.length === 0 &&
      terms.length > 0 &&
      (isFirstVisit || isSwitchingSearchType)
    ) {
      if (isFirstVisit) setIsFirstVisit(false);
      if (isSwitchingSearchType) setIsSwitchingSearchType(false);
      setSelectedTerms(terms);
      // Fetch data for all terms on initial load
      spinnerService.show('mySpinner');
      const fetchAllTerms = async () => {
        for (const term of terms) {
          const itemsForTerm = filtered.filter((f) => f.term === term);
          if (itemsForTerm.length > 0) {
            const searchParams = {
              term,
              [currentSearchType === 'class'
                ? 'classNbr'
                : 'subjectnumberlist']: itemsForTerm.map((f) => f.id).join(','),
            };
            const response =
              currentSearchType === 'class'
                ? await getSearchResults(searchParams)
                : await getCourseSearchResults(searchParams);

            // Only update if search type hasn't changed
            if (currentSearchType === currentSearchTypeRef.current) {
              setFavoriteItemsByTerm((prev) => {
                const updated = new Map(prev);
                updated.set(
                  term,
                  currentSearchType === 'class'
                    ? response?.classes || []
                    : response || []
                );
                return updated;
              });
            }
          }
        }
        // Hide spinner after all terms are processed or if no items to fetch
        if (currentSearchType === currentSearchTypeRef.current) {
          spinnerService.hide('mySpinner');
        }
      };
      fetchAllTerms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favorites, searchType, selectedTerms.length]);

  // Fetch detailed class/course data for newly added terms
  useEffect(() => {
    // Only fetch data for newly added term
    if (!newlyAddedTerm) return;

    const currentSearchType = searchType; // Capture current search type
    spinnerService.show('mySpinner');

    if (selectedTerms.length === 0) {
      setFavoriteItemsByTerm(new Map());
      spinnerService.hide('mySpinner');
      return;
    }
    const fetchItems = async () => {
      const itemsForTerm = filteredFavorites.filter(
        (f) => f.term === newlyAddedTerm
      );

      if (itemsForTerm.length > 0) {
        const searchParams = {
          term: newlyAddedTerm,
          [currentSearchType === 'class' ? 'classNbr' : 'courseNbr']:
            itemsForTerm.map((f) => f.id).join(','),
        };
        // console.log('Search params for', newlyAddedTerm, searchParams);
        const response =
          currentSearchType === 'class'
            ? await getSearchResults(searchParams)
            : await getCourseSearchResults(searchParams);
        // console.log('Response', response);

        // Only update if search type hasn't changed
        if (currentSearchType === currentSearchTypeRef.current) {
          // Add new term data to existing results
          setFavoriteItemsByTerm((prev) => {
            const updated = new Map(prev);
            updated.set(
              newlyAddedTerm,
              currentSearchType === 'class'
                ? response?.classes || []
                : response || []
            );
            return updated;
          });

          // Auto-scroll to newly added term section
          setTimeout(() => {
            const element = document.getElementById(`term-${newlyAddedTerm}`);
            if (element) {
              const elementRect = element.getBoundingClientRect();
              const offset = 100; // Offset to show more of the header
              window.scrollTo({
                top: window.pageYOffset + elementRect.top - offset,
                behavior: 'smooth',
              });
            }
            setNewlyAddedTerm(null);
          }, 100);
        }
      }

      if (currentSearchType === currentSearchTypeRef.current) {
        spinnerService.hide('mySpinner');
      }
    };

    fetchItems();
  }, [filteredFavorites, searchType, newlyAddedTerm, selectedTerms.length]);

  // Don't render anything until auth check is complete
  if (!isAuthenticated) {
    return null;
  }

  // Handle search type toggle between class and course
  const handleSearchTypeChange = () => {
    const newSearchType = searchType === 'class' ? 'course' : 'class';
    currentSearchTypeRef.current = newSearchType;
    // Hide spinner in case it's showing
    spinnerService.hide('mySpinner');
    // Clear existing data immediately to prevent race conditions
    setFavoriteItemsByTerm(new Map());
    setSelectedTerms([]);
    // Mark that we're switching search types to trigger auto-selection
    setIsSwitchingSearchType(true);
    setSearchType(newSearchType);
  };

  // Function to refresh favorites data
  const refreshFavorites = async () => {
    const result = await getFavorites();
    const output = result?.output || [];
    setFavorites(output);
  };

  // Handle term selection/deselection in dropdown
  const handleTermChange = async (term, checked) => {
    if (checked) {
      // Refresh favorites to get latest data before adding term
      await refreshFavorites();
      // Add term to selection and mark for scrolling
      setSelectedTerms((prev) => [...prev, term]);
      setNewlyAddedTerm(term);
    } else {
      // Remove term from selection and clear cached data
      setSelectedTerms((prev) => prev.filter((t) => t !== term));
      setFavoriteItemsByTerm((prev) => {
        const updated = new Map(prev);
        updated.delete(term);
        return updated;
      });
    }
  };

  // Track odd/even rows for styling
  let odd = false;
  return (
    <>
      <Container fluid={true} className="p-0 ">
        <Header navTree={navTree} />
        <section
          style={jumbotronBackground}
          className="mb-0 pt-10 pb-10"
          id="main"
          aria-label="My favorites page"
        >
          <Container>
            <Row>
              <Col xs={'auto'} className="p-0">
                <h1 className="mt-0" id="skip-to-content">
                  <span className="highlight-white">My favorites</span>
                </h1>
              </Col>
            </Row>
          </Container>
        </section>
        {/* Main content area */}
        <Container fluid={true} className="class-results ps-5">
          <Row className=" pt-5 pb-3 ps-5">
            <Col>
              <h2>My Favorites</h2>
            </Col>
          </Row>
          {/* Filter controls row */}
          <Row className="ps-5">
            {/* Class/Course toggle switch */}
            <Col xs={'auto'} key="searchType" className="mb-2">
              <div className="btn-container compact" tabIndex={-1}>
                <label
                  className="switch btn-color-mode-switch compact"
                  tabIndex={-1}
                >
                  <input
                    role="switch"
                    type="checkbox"
                    name="searchType"
                    id="color_mode"
                    onChange={handleSearchTypeChange}
                    value="class"
                    checked={searchType === 'course'}
                    tabIndex={0}
                    //onFocus={this.handleSwitchFocus}
                    // onBlur={this.handleSwitchBlur}
                    aria-label={`Toggle between classes and courses favorites, ${
                      searchType === 'course'
                        ? 'Courses selected'
                        : 'Classes selected'
                    }`}
                  />
                  <label
                    aria-hidden="true"
                    //ref={this.switchRef}
                    htmlFor="color_mode"
                    data-on="Courses"
                    data-off="Classes"
                    className="btn-color-mode-switch-inner"
                  ></label>
                </label>
              </div>
            </Col>
            {/* Term selection dropdown - only show if there are favorites */}
            {termList.length > 0 && (
              <Col className="">
                <form className="admin uds-form" id="skip-to-content">
                  <Dropdown className="filter-dropdown">
                    <Dropdown.Toggle
                      bsPrefix={`custom-dropdown-toggle${
                        selectedTerms.length > 0 ? ' active' : ''
                      }`}
                      variant="filter"
                      aria-label={
                        selectedTerms.length > 0
                          ? selectedTerms.length <= 2
                            ? selectedTerms
                                .map((term) =>
                                  UtilFunctions.getStringFromTermcode(term)
                                )
                                .join(', ')
                            : selectedTerms
                                .slice(0, 2)
                                .map((term) =>
                                  UtilFunctions.getStringFromTermcode(term)
                                )
                                .join(', ') +
                              ` +${selectedTerms.length - 2} additional options selected`
                          : 'Select term(s)'
                      }
                    >
                      {/* Display selected terms or placeholder text */}
                      {selectedTerms.length > 0
                        ? selectedTerms.length <= 2
                          ? selectedTerms
                              .map((term) =>
                                UtilFunctions.getStringFromTermcode(term)
                              )
                              .join(', ')
                          : selectedTerms
                              .slice(0, 2)
                              .map((term) =>
                                UtilFunctions.getStringFromTermcode(term)
                              )
                              .join(', ') + ` +${selectedTerms.length - 2}`
                        : 'Select term(s)'}
                      <div className="pull-right ms-1">
                        <FontAwesomeIcon icon={faChevronDown} />
                      </div>
                    </Dropdown.Toggle>

                    {/* Dropdown menu with term checkboxes */}
                    <Dropdown.Menu>
                      {termList.map((theTerm) => (
                        <React.Fragment key={theTerm}>
                          <Form.Check
                            type="checkbox"
                            label={UtilFunctions.getStringFromTermcode(theTerm)}
                            checked={selectedTerms.includes(theTerm)}
                            id={theTerm}
                            onChange={(e) =>
                              handleTermChange(theTerm, e.target.checked)
                            }
                            name="term"
                            className="filter-check mb-2"
                            tabIndex={0}
                          />
                        </React.Fragment>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </form>
              </Col>
            )}
          </Row>

          {/* Results display section */}
          <Row className="p-5">
            <Col>
              {/* Show message when no favorites exist - only after initial load is complete */}
              {isInitialLoadComplete && termList.length === 0 && (
                <div className="text-center py-5">
                  <h4 className="mb-3">
                    No favorite {searchType === 'class' ? 'classes' : 'courses'}{' '}
                    yet!
                  </h4>
                </div>
              )}

              {favoriteItemsByTerm && favoriteItemsByTerm.size > 0 && (
                <div id="class-results">
                  {/* Display results grouped by term in dropdown order */}
                  {termList
                    .filter(
                      (term) =>
                        favoriteItemsByTerm.has(term) &&
                        selectedTerms.includes(term)
                    )
                    .map((term) => {
                      const items = favoriteItemsByTerm.get(term) || [];
                      odd = false; // Reset odd/even counter for each term
                      return (
                        <div key={term} className="mb-5" id={`term-${term}`}>
                          {/* Term header */}
                          <h3>
                            <span className="highlight-gold">
                              {UtilFunctions.getStringFromTermcode(term)}
                            </span>
                          </h3>
                          {/* Class results table */}
                          {searchType === 'class' && (
                            <div className="class-results-rows">
                              {UtilFunctions.getClassesTableColumnHeaders()}
                              {items.map((myClass, index) => {
                                odd = !odd; // Alternate row styling
                                // Only render ClassRow if it has valid class data structure
                                if (
                                  myClass &&
                                  myClass.CLAS &&
                                  myClass.CLAS.CLASSNBR
                                ) {
                                  return (
                                    <ClassRow
                                      classItem={myClass}
                                      key={index}
                                      searchType="ALL"
                                      oddEvenClass={odd}
                                      favorites={favorites}
                                      term={term}
                                      onFavoriteChange={refreshFavorites}
                                    />
                                  );
                                }
                                return null;
                              })}
                            </div>
                          )}
                          {/* Course results table */}
                          {searchType === 'course' && (
                            <MediaQuery query="(min-device-width: 576px)">
                              {UtilFunctions.getSubjectsTitle()}
                              <ListGroup variant="flush">
                                {items.map((myCourse, index) => {
                                  const oddClass =
                                    index % 2 === 0 ? 'even' : 'odd';
                                  return (
                                    <ListGroup.Item
                                      key={index}
                                      variant={oddClass}
                                    >
                                      <CourseItem
                                        course={myCourse}
                                        collapse={undefined}
                                        term={term}
                                        favorites={favorites}
                                        onFavoriteChange={refreshFavorites}
                                      />
                                    </ListGroup.Item>
                                  );
                                })}
                              </ListGroup>
                            </MediaQuery>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </Container>
      <Footer />
      <span className="spinner-span">
        <Spinner name="mySpinner">
          <div
            variant="primary"
            disabled
            className="spinner-button mt-5 spinner-button-styles"
          >
            <SpinnerImage
              as="span"
              animation="grow"
              size="sm"
              role="status"
              aria-hidden="true"
              className="me-2"
            />
            Loading ...
          </div>
        </Spinner>
      </span>
    </>
  );
};

export default Favorites;

const getFavorites = async () => {
  try {
    const response = await fetch(
      serviceauth.getRequestObject(UtilFunctions.getFavoritesUrl(), 'GET')
    );
    if (response.status === 401) serviceauth.reauth();
    return await response.json();
  } catch (e) {
    console.error('Failed to fetch favorites:', e);
    return { output: [] };
  }
};

const getSearchResults = async (params) => {
  try {
    const response = await fetch(
      serviceauth.getRequestObject(
        UtilFunctions.getClassSearchUrl(params, []),
        'GET'
      )
    );
    if (response.status === 401) serviceauth.reauth();
    return await response.json();
  } catch (e) {
    console.error('Failed to fetch search results:', e);
    return null;
  }
};

const getCourseSearchResults = async (params) => {
  try {
    const url = UtilFunctions.getCourseSearchUrl(params);
    // console.log('URL----> ', url);
    // console.log('params----> ', params);

    const response = await fetch(serviceauth.getRequestObject(url, 'GET'));
    if (response.status === 401) serviceauth.reauth();
    const result = await response.json();
    // console.log('Result size----> ', result.length);
    return result;
  } catch (e) {
    console.error('Failed to fetch course results:', e);
    return [];
  }
};
